import { useEffect, useRef, useState } from 'react';
import { useInView, useMotionValue, useSpring } from 'framer-motion';

/**
 * Scroll-triggered animated counter.
 * Extracts the numeric portion from a display value like '500+' or '3.4x' or '−28%',
 * animates from 0 up to that number when the element enters the viewport,
 * and preserves any prefix/suffix (%, +, x, etc.).
 */
export default function AnimatedCounter({ value, duration = 2, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [display, setDisplay] = useState('0');

  // Parse value like '4.2x' -> { prefix: '', number: 4.2, suffix: 'x', decimals: 1 }
  const parsed = (() => {
    const raw = String(value);
    const match = raw.match(/^([^\d.-]*)(-?[\d.,]+)(.*)$/);
    if (!match) return { prefix: '', number: 0, suffix: raw, decimals: 0 };
    const prefix = match[1] || '';
    const cleanNum = match[2].replace(/,/g, '');
    const number = parseFloat(cleanNum);
    const suffix = match[3] || '';
    const decimals = (cleanNum.split('.')[1] || '').length;
    return { prefix, number, suffix, decimals };
  })();

  useEffect(() => {
    if (!inView) return;
    let raf;
    const startTs = performance.now();
    const from = 0;
    const to = parsed.number;

    const tick = (now) => {
      const elapsed = (now - startTs) / 1000;
      const t = Math.min(1, elapsed / duration);
      // ease-out cubic for a satisfying count deceleration
      const eased = 1 - Math.pow(1 - t, 3);
      const current = from + (to - from) * eased;
      const formatted = parsed.decimals > 0
        ? current.toFixed(parsed.decimals)
        : Math.round(current).toLocaleString();
      setDisplay(`${parsed.prefix}${formatted}${parsed.suffix}`);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, duration, parsed.number, parsed.decimals, parsed.prefix, parsed.suffix]);

  return (
    <span ref={ref} className={className}>{display}</span>
  );
}
